import React from 'react';
import PropTypes from 'prop-types';
import { graphql, Link } from 'gatsby';
import { get } from 'lodash';
import slugify from 'slugify';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import Layout from '~components/Layout';
import BodyElement from '~components/body/BodyElement';
import ArchieElement from '~components/body/ArchieElement';
import SeoSocial from '~components/base/SeoSocial';

import useBusiness from '~lib/useBusiness';
import Header from '~components/base/Header';
import Banner from '~components/graphics/Banner';
import Button from '~components/graphics/Button';
import Explore from '~components/graphics/Explore';
import Sidebar from '~components/graphics/Sidebar';

const ArticleStyles = styled.div`
    display: grid;
    grid-template-columns: 400px 1fr;
    grid-column-gap: 48px;
    padding: 24px;
    padding-top: 48px;
    position: relative;
    z-index: 1;

    .article__byline {
      text-transform: uppercase;
      font-family: BentonSansCond;
      font-weight: 400;
      border-bottom: 5px solid #F37D56;
      font-size: 20px;
      display: inline-block;
      margin-bottom: 24px;
    }

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }

    @media (max-width: 640px) {
        padding-top: 24px;
    }
`;

export const pageQuery = graphql`
    query($storyId: String!) {
        site {
          siteMetadata {
            title
            sectionName
            projectName
          }
        }
        sheet: allGoogleSheetSheet1Sheet1(sort: {fields: order}) {
          nodes {
            storyId
            arcId
            color
            slug
            slugPrev
            slugNext
            image
            title
            desc
            byline
          }
        }
        story: arcStory(storyId: {eq: $storyId}) {
            headlines {
                basic
            }
            subheadlines {
                basic
            }
            credits {
                name
            }
            description {
              basic
            }
            allPromoImages {
                socialMedia {
                    sizes {
                        _2048
                    }
                }
            }
            bodyElements {
              _id
              type
              subtype
              alignment
              content
              archieJson
              url
              srcSet
              caption
              subtype
              bodyElements {
                _id
                content
              }
              citation {
                content
              }
            }
            relatedLinks {
              headlines {
                basic
              }
              promoImage {
                sizes {
                  _1024
                  _480
                  _640
                  _240
                }
                srcSet
                url
              }
              url
            }
            slug
        }
    }
`;

const ArticleTemplate = ({
  data: { 
    story, 
    sheet,
    site: {
      siteMetadata: { sectionName, projectName },
    },
  },
  pageContext: { storyId },
  path,

}) => {
  const subhed = get(story, 'subheadlines.basic')
    ? (<h3 className="subheadline miller-n4">{get(story, 'subheadlines.basic')}</h3>)
    : (null);
    const storySlug = story.slug;
    const storyData = sheet.nodes.filter((obj) => obj.slug === storySlug);

    let prevSlug = get(storyData, '0.slugPrev', null);
        if (prevSlug === 'N/A') {
          prevSlug = null;
        }
    if (story.storyId === 'a1') {
      prevSlug = '/';
    }
    
    let nextSlug = get(storyData, '0.slugNext', null);
    if (nextSlug === 'N/A') {
      nextSlug = null;
    }

    const slugifiedSection = slugify(sectionName, { lower: true });
    const authorNames = get(story, 'credits', [])
      .map((credit) => credit.name)
      .join(', ');

    useBusiness({
      pageName: `${slugifiedSection} | ${get(story, 'headlines.basic')}`,
      environment: process.env.NODE_ENV,
      meterContent: false,
      pageId: `apps.${story.slug}`,
      section: slugifiedSection,
      author: authorNames,
      projectName,
    });
    const seoTitle = `Editing the Constitution | ${get(story, 'headlines.basic')}`;
    const seoUrl = new URL(`/ideas/graphics/2021/12/editing-the-constitution/${story.slug}`, 'https://apps.bostonglobe.com').toString();
  return (
    <Layout>
      <SeoSocial
        url={seoUrl}
        description={get(story, 'description.basic')}
        title={seoTitle}
        imageUrl={get(story, 'allPromoImages.socialMedia.sizes._2048')}
      />
      <Banner />

      <ArticleStyles>
        <Sidebar data={sheet} />

        <main>
          <div className="btn-wrapper">
            <Link to="/"><button className="btn-read">Read the introduction</button></Link>
            <a href="#explore"><button className="btn-explore">Explore this series</button></a>
          </div>

          <h1>{get(story, 'headlines.basic')}</h1>
          {subhed}
          <p className="article__byline">by {story.credits[0].name}</p>

          {story.bodyElements.map((element, index) => {
            if (element.archieJson) {
              return (
                <ArchieElement
                  data={element}
                  key={element._id}
                  relatedLinks={get(story, 'relatedLinks')}
                />
              );
            }
            return (
              <BodyElement
                data={element}
                key={element._id}
                dropCap={index === 0}
              />
            );
          })}

          <Button next={nextSlug} prev={prevSlug}/>
        </main>

      </ArticleStyles>

      <Explore data={sheet}/>
    </Layout>
  );
};

ArticleTemplate.propTypes = {
  data: PropTypes.shape({
    story: PropTypes.shape({
      bodyElements: PropTypes.arrayOf(
        PropTypes.shape({
          archieJson: PropTypes.string,
        }),
      ),
      allPromoImages: PropTypes.shape({
        socialMedia: PropTypes.shape({
          sizes: PropTypes.shape({
            _2048: PropTypes.string,
          }),
        }),
      }),
      slug: PropTypes.string,
    }),
    site: PropTypes.shape({
      siteMetadata: PropTypes.shape({
        title: PropTypes.string.isRequired,
        sectionName: PropTypes.string.isRequired,
        projectName: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({
    storyId: PropTypes.string,
  }).isRequired,
};

export default ArticleTemplate;
